import { useCallback, useState, useMemo } from 'react';
import type { MonitoredSite, CreateSiteRequest, UpdateSiteRequest, CheckResult } from '../types';
import * as monitorApi from '../services/monitorApi';
import { withAuthRetry } from '../utils/withAuthRetry';

interface UseMonitorProps {
  accessToken?: string;
  refreshToken?: string;
  attemptRefresh: () => Promise<boolean>;
}

export function useMonitor({ accessToken, refreshToken, attemptRefresh }: UseMonitorProps) {
  const [sites, setSites] = useState<MonitoredSite[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSite, setSelectedSite] = useState<MonitoredSite | null>(null);

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  // History state
  const [historyResults, setHistoryResults] = useState<CheckResult[]>([]);
  const [uptimePercentage, setUptimePercentage] = useState<number>(0);
  const [avgResponseTime, setAvgResponseTime] = useState<number>(0);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Trigger check state
  const [checkingId, setCheckingId] = useState<string | null>(null);

  const fetchSites = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };

    setLoading(true);

    try {
      const items = await withAuthRetry(
        () => monitorApi.getSites(accessToken!),
        refreshToken,
        attemptRefresh
      );
      setSites(items);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro inesperado ao buscar sites'
      };
    } finally {
      setLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh]);

  const fetchSiteHistory = useCallback(async (siteId: string): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };

    setHistoryLoading(true);

    try {
      const data = await withAuthRetry(
        () => monitorApi.getSiteHistory(siteId, accessToken!),
        refreshToken,
        attemptRefresh
      );
      setHistoryResults(data.results);
      setUptimePercentage(data.uptimePercentage);
      setAvgResponseTime(data.avgResponseTime);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro ao buscar histórico'
      };
    } finally {
      setHistoryLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh]);

  const createSite = useCallback(async (data: CreateSiteRequest): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };

    try {
      await withAuthRetry(
        () => monitorApi.createSite(data, accessToken!),
        refreshToken,
        attemptRefresh
      );
      setAddDialogOpen(false);
      await fetchSites();
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro ao criar site'
      };
    }
  }, [accessToken, refreshToken, attemptRefresh, fetchSites]);

  const updateSite = useCallback(async (
    id: string,
    data: UpdateSiteRequest
  ): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };

    try {
      await withAuthRetry(
        () => monitorApi.updateSite(id, data, accessToken!),
        refreshToken,
        attemptRefresh
      );
      setEditDialogOpen(false);
      setSelectedSite(null);
      await fetchSites();
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro ao atualizar site'
      };
    }
  }, [accessToken, refreshToken, attemptRefresh, fetchSites]);

  const deleteSite = useCallback(async (id: string): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };

    try {
      await withAuthRetry(
        () => monitorApi.deleteSite(id, accessToken!),
        refreshToken,
        attemptRefresh
      );
      setDeleteDialogOpen(false);
      setSelectedSite(null);
      await fetchSites();
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro ao excluir site'
      };
    }
  }, [accessToken, refreshToken, attemptRefresh, fetchSites]);

  const triggerCheck = useCallback(async (id: string): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };

    setCheckingId(id);

    try {
      await withAuthRetry(
        () => monitorApi.triggerCheck(id, accessToken!),
        refreshToken,
        attemptRefresh
      );
      // Refresh sites after a short delay to allow check to complete
      setTimeout(() => {
        void fetchSites();
      }, 1000);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro ao verificar site'
      };
    } finally {
      setCheckingId(null);
    }
  }, [accessToken, refreshToken, attemptRefresh, fetchSites]);

  const openAddDialog = useCallback(() => {
    setAddDialogOpen(true);
  }, []);

  const openEditDialog = useCallback((site: MonitoredSite) => {
    setSelectedSite(site);
    setEditDialogOpen(true);
  }, []);

  const openDeleteDialog = useCallback((site: MonitoredSite) => {
    setSelectedSite(site);
    setDeleteDialogOpen(true);
  }, []);

  const openHistoryDialog = useCallback((site: MonitoredSite) => {
    setSelectedSite(site);
    setHistoryDialogOpen(true);
    void fetchSiteHistory(site.id);
  }, [fetchSiteHistory]);

  // Update site in real-time from WebSocket
  const updateSiteFromWebSocket = useCallback((siteId: string, checkResult: Partial<CheckResult>) => {
    setSites(prev => prev.map(site => {
      if (site.id === siteId && site.latestCheck) {
        return {
          ...site,
          latestCheck: {
            ...site.latestCheck,
            ...checkResult
          }
        };
      }
      return site;
    }));
  }, []);

  return {
    // State
    sites,
    loading,
    selectedSite,
    addDialogOpen,
    setAddDialogOpen,
    editDialogOpen,
    setEditDialogOpen,
    deleteDialogOpen,
    setDeleteDialogOpen,
    historyDialogOpen,
    setHistoryDialogOpen,
    historyResults,
    historyLoading,
    uptimePercentage,
    avgResponseTime,
    checkingId,

    // Actions
    fetchSites,
    fetchSiteHistory,
    createSite,
    updateSite,
    deleteSite,
    triggerCheck,
    openAddDialog,
    openEditDialog,
    openDeleteDialog,
    openHistoryDialog,
    updateSiteFromWebSocket
  };
}
